import IconButton from '@mui/material/IconButton';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Tooltip from '@mui/material/Tooltip';
import Box from '@mui/material/Box';
import { keyframes } from '@emotion/react';
import { IconStyle, CnpjNumber, ClientName, TicketNumber, ViewButton } from 'components/Shared/Table/styles';
import React from 'react';
import { Link } from 'react-router-dom';
import { MUIDataTableMeta } from 'mui-datatables';
import { getColorFromString } from 'helpers/utils';
import { INCOMPLETE_DATA_MESSAGE } from 'helpers/constants';

const pulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.5); opacity: 0.4; }
`;

const COLUMN_NAMES = [
  'id',
  'nickname',
  'isReportSource',
  'cnpj',
  'number',
  'process_number',
  'datetime',
  'type',
  'subtype',
  'status',
  'actions'
] as const;

const getColumnValue = (tableMeta: MUIDataTableMeta, columnName: typeof COLUMN_NAMES[number]) =>
  tableMeta.rowData[COLUMN_NAMES.indexOf(columnName)];

export const columns = [
  {
    name: 'id',
    label: 'id',
    options: {
      filter: false,
      sort: false,
      display: false
    }
  },
  {
    name: 'nickname',
    label: 'Apelido',
    options: {
      filter: false,
      sort: false,
      display: false
    }
  },
  {
    name: 'isReportSource',
    label: 'Dados incompletos',
    options: {
      filter: false,
      sort: false,
      display: false
    }
  },
  {
    name: 'cnpj',
    label: 'CNPJ',
    options: {
      filter: true,
      sort: true,
      customBodyRender: (value: string, tableMeta: MUIDataTableMeta) => {
        return (
          <>
            <span style={CnpjNumber}>{value}</span>
            <br />
            <span style={ClientName}>{getColumnValue(tableMeta, 'nickname')}</span>
          </>
        );
      }
    }
  },
  {
    name: 'number',
    label: 'Auto de Infração',
    options: {
      filter: true,
      sort: true,
      customBodyRender: (value: string, tableMeta: MUIDataTableMeta) => {
        const isReportSource = Boolean(getColumnValue(tableMeta, 'isReportSource'));

        if (!isReportSource) {
          return <span style={TicketNumber}>{value}</span>;
        }

        return (
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
            <Box component='span' sx={{ color: 'warning.main', fontWeight: 600 }}>
              {value}
            </Box>
            <Tooltip
              title={INCOMPLETE_DATA_MESSAGE}
              placement='top'
              arrow
              componentsProps={{ tooltip: { sx: { whiteSpace: 'pre-line' } } }}
            >
              <InfoOutlinedIcon
                role='img'
                aria-label={INCOMPLETE_DATA_MESSAGE}
                tabIndex={0}
                sx={{ fontSize: 16, color: 'warning.main', cursor: 'help' }}
              />
            </Tooltip>
          </Box>
        );
      }
    }
  },
  {
    name: 'process_number',
    label: 'Nº do Processo',
    options: {
      filter: true,
      sort: true
    }
  },
  {
    name: 'datetime',
    label: 'Data da Infração',
    options: {
      filter: true,
      sort: true
    }
  },
  {
    name: 'type',
    label: 'Tipo',
    options: {
      filter: true,
      sort: true
    }
  },
  {
    name: 'subtype',
    label: 'Subtipo',
    options: {
      filter: true,
      sort: true
    }
  },
  {
    name: 'status',
    label: 'Status',
    options: {
      filter: true,
      sort: true,
      customBodyRender: (value: string) => {
        const statusColor = getColorFromString(value);

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                backgroundColor: statusColor,
                flexShrink: 0,
                animation: `${pulse} 1.2s ease-in-out infinite`,
                marginTop: 0.3
              }}
            />
            <span style={{ color: statusColor, fontWeight: 600 }}>{value}</span>
          </Box>
        );
      }
    }
  },
  {
    name: 'actions',
    label: 'Ações',
    options: {
      filter: false,
      sort: false,
      empty: true,
      customBodyRender: (value: string, tableMeta: MUIDataTableMeta) => {
        return (
          <Tooltip title='Visualizar' placement='left-start' style={ViewButton}>
            <Link to={`/multas/${getColumnValue(tableMeta, 'id')}`}>
              <IconButton aria-label='view'>
                <RemoveRedEyeIcon style={IconStyle} />
              </IconButton>
            </Link>
          </Tooltip>
        );
      }
    }
  }
];

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;

export const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250
    }
  }
};

export type FilterItem = {
  name: string;
  order?: string;
  filter?: unknown;
};

export type ToggleItem = {
  name: string;
};

export const filterByOptions = [
  {
    value: 'occurrence',
    label: 'Data de Ocorrência'
  },
  {
    value: 'due_date',
    label: 'Data de Vencimento'
  }
];

export const filterByPeriodOptions = [
  {
    value: 'today',
    label: 'Hoje'
  },
  {
    value: 'week',
    label: 'Essa semana'
  },
  {
    value: 'month',
    label: 'Esse mês'
  },
  {
    value: 'year',
    label: 'Esse Ano'
  },
  {
    value: '30',
    label: 'Últmos 30 dias'
  },
  {
    value: '365',
    label: 'Últimos 12 meses'
  },
  {
    value: 'custom',
    label: 'Personalizado'
  }
];
